import React from 'react';
import { ISummary, ISummaryItem } from 'base/interfaces/order';
import cn from 'classnames';
import ThemeTooltip from '@app/common/ThemeTooltip';
import site from 'pro/config/site';
import { useTrans } from 'base/utils/i18n';
import { LoopRender } from '@base/snippets/LoopRender';
import { HtmlRender } from '@base/snippets/HtmlRender';

export type OrderSummaryProps = {
    orderId?: string;
    data?: ISummary;
    className?: string;
    titleClassName?: string;
    missShow?: string[];
    title?: false | React.ReactNode;
    children?: React.ReactNode;
};

const SummaryItem: React.FC<{ data: ISummaryItem; isDel: boolean; }> = ({ data, isDel }) => {
    return (
        <li key={data.key} className={cn(`g-${data.key}`, 'g-detail flex flex-justify flex-top')}>
            <div className={'g-label'}>{data.displayName}</div>

            {isDel
                ? <del className={'g-value'}>{data.displayValue}</del>
                : (
                    <div className={cn('g-value', { 'g-highlight': data.negative || data.highlight })}>
                        {data.negative && <span>-</span>}
                        {data.displayValue}
                    </div>
                )}
        </li>
    )
};

const SummaryTotal: React.FC<{ data: ISummary['total']; }> = ({ data }) => {
    const { t } = useTrans();
    return !data ? null : (
        <li className={'flex flex-justify flex-top g-total'}>
            <div className={'g-label'}>{data.displayName}</div>
            <div className={'g-value g-total-value'}>
                {data.displayValue}

                {!!data.tradeInPoints && (
                    <div className="g-trade-in-points">
                        {t('web_common_plus_points', { num: data.tradeInPoints })}
                    </div>
                )}
            </div>
        </li>
    )
};

export const OrderSummary: React.FC<OrderSummaryProps> = props => {
    const { t } = useTrans();
    const {
        orderId,
        children,
        data,
        className,
        titleClassName,
        missShow = [],
        title = t('web_summary_title_order_summary'),
    } = props;

    return !data ? null : (
        <div className={className}>
            {!!title && <h2 className={cn(titleClassName, 'g-summary-title')}>{title}</h2>}

            <ul className="g-summary-items">
                {orderId && (
                    <li key={'orderId'} className={cn('g-detail flex flex-justify flex-bottom')}>
                        <span className={'g-label'}>{t('web_summary_order_no')}</span>
                        <span className={'g-value'}>{orderId}</span>
                    </li>
                )}

                <LoopRender
                    tag={false}
                    list={data.details.filter(item => !missShow.includes(item.key))}
                    render={item => item.isTotal
                        ? <SummaryTotal data={item}/>
                        : (
                            <SummaryItem
                                data={item}
                                isDel={item.key === 'retail' && item.displayValue !== data.details.find(item => item.key === 'subtotal')?.displayValue}
                            />
                        )
                    }
                />

                {!!data.total && !missShow.includes('total') && <SummaryTotal data={data.total}/>}

                <LoopRender
                    tag={false}
                    list={data.rewards}
                    render={item => (
                        <li className={'g-reward'}>
                            <HtmlRender
                                tag={'span'}
                                html={item.title.replace('{{value}}', `<span class="g-highlight">${item.value}</span>`)}
                            />
                            {' '}
                            <ThemeTooltip
                                title={t('web_summary_tip_reward_points_tooltip', { brand: site.brand })}
                                placement="bottomRight"
                                arrowPointAtCenter
                            />
                        </li>
                    )}
                />
            </ul>
            {children}
        </div>
    );
};